import { NodeTree, ROOT_NODE, SerializedNode, useEditor } from "@craftjs/core";
import { useCallback } from "react";
import scrollIntoView from "scroll-into-view-if-needed";
import { v6 as uuid } from "uuid";

import type { CraftQuery } from "@/components/editor/themes/types";

import type { BrandingPreset, Page } from "../page/domain";
import getHazelTemplate from "../theme/themes/hazel/default-page";

const useScrollToNode = () => {
  const { query } = useEditor();

  const scrollToNode = useCallback(
    (nodeId: string) => {
      const nodeQuery = query.node(nodeId);

      const dom = nodeQuery.getDOM();
      if (dom) {
        scrollIntoView(dom, { behavior: "smooth" });
      }
    },
    [query]
  );

  return scrollToNode;
};

export default useScrollToNode;

const rootNode: SerializedNode = {
  type: "PageContainer",
  nodes: [],
  custom: {},
  hidden: false,
  isCanvas: true,
  displayName: "PageContainer",
  linkedNodes: {},
  props: {
    style: {
      minHeight: "100vh",
    },
  },
  parent: undefined,
};

export function createDefaultPage(brandingPreset: BrandingPreset): Page {
  return {
    slug: uuid(),
    content: {
      [ROOT_NODE]: {
        ...rootNode,
      },
    },
    published: false,
    revision: 1,
    themeKind: "hazel",
    brandingPreset,
    metadata: {
      title: "My content hub",
    },
    gatingType: "OPEN_ACCESS",
  };
}

/**
 * Get the template blocks for a given theme
 * @note If we add more themes, we should make the load dynamic
 * (await import("../theme/themes/hazel/default-page")).default()
 *
 */
const getTemplate = async (themeName: string) => {
  switch (themeName.toLowerCase()) {
    case "hazel":
      return getHazelTemplate();
    default:
      return [];
  }
};

export async function themeTemplateFactory(
  themeName: string,
  query: CraftQuery,
  addNodeTree: (nodeTree: NodeTree) => void
) {
  const templateBlocks = await getTemplate(themeName);

  const pageTemplate = templateBlocks.reduce<{
    nodeIds: string[];
    nodes: Record<string, SerializedNode>;
  }>(
    (acc, block) => {
      const blockNodeTree = query.parseReactElementAsNodeTree(block);

      acc.nodeIds.push(blockNodeTree.rootNodeId);
      acc.nodes = {
        ...acc.nodes,
        ...blockNodeTree.nodes,
      };

      return acc;
    },
    { nodeIds: [], nodes: {} }
  );

  const emptyPage = query.node(ROOT_NODE).toNodeTree();
  const rootNode = emptyPage.nodes[ROOT_NODE];

  addNodeTree({
    ...emptyPage,
    nodes: {
      [ROOT_NODE]: {
        ...rootNode,
        nodes: [...pageTemplate.nodeIds],
      },
      ...pageTemplate.nodes,
    },
  });
}
